import { RUNTIME_CONFIG } from './runtimeConfig';

// API Configuration
export const API_CONFIG = {
  BASE_URL: RUNTIME_CONFIG.API_BASE_URL,
  REQUEST_TIMEOUT_MS: Number(process.env.REACT_APP_API_TIMEOUT_MS || 12000),
  ENDPOINTS: {
    USERS: {
      REGISTER: '/users/signup',
    },
    AUTH: {
      LOGIN: '/users/login',
      LOGOUT: '/users/logout',
      REFRESH: '/users/refresh',
      ME: '/users/me',
      LOGOUT_ALL: '/users/logout-all'
    },
    OTP: {
      SEND_EMAIL: '/otp/send/email',
      SEND_MOBILE: '/otp/send/mobile',
      VERIFY_EMAIL: '/otp/verify/email',
      VERIFY_MOBILE: '/otp/verify/mobile',
    },
    BOOKING: {
      LIST: '/bookings',
      CREATE: '/bookings',
      GET_BY_ID: (id) => `/bookings/${id}`,
      UPDATE: (id) => `/bookings/${id}`,
      DELETE: (id) => `/bookings/${id}`,
      UPDATE_STATUS: (id, status) => `/bookings/${id}/status?status=${encodeURIComponent(status)}`,
      LIST_BY_PANDIT: (panditId) => `/bookings?panditId=${encodeURIComponent(panditId)}`,
      LIST_BY_USER: (userId) => `/bookings?userId=${encodeURIComponent(userId)}`
    },
    ONBOARDING: {
      LIST: '/admin/onboarding-requests',
      GET_BY_ID: (id) => `/admin/onboarding-requests/${id}`,
      UPDATE_STATUS: (id) => `/admin/onboarding-requests/${id}/status`,
      ASSIGN_REVIEWER: (id) => `/admin/onboarding-requests/${id}/assign-reviewer`
    },
    PANCHANG: {
      HIGHLIGHTS: '/panchang/highlights'
    },
    CATALOG: {
      PANDITS: '/catalog/pandits',
      PANDIT_BY_USER: (userId) => `/catalog/pandits/${userId}`,
      CATEGORY_CARDS: '/catalog/service-categories/cards',
      CATEGORY_SERVICES: (categoryId) => `/catalog/service-categories/${encodeURIComponent(categoryId)}/services`
    },
    AVAILABILITY: {
      CREATE_SLOT: '/availability/slots',
      MY_SLOTS: '/availability/slots/me',
      SEARCH_SLOTS: '/availability/slots/search',
      DELETE_SLOT: (slotId) => `/availability/slots/${slotId}`
    }
  },
  HTTP_METHODS: {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    PATCH: 'PATCH',
    DELETE: 'DELETE',
  },
  STATUS_CODES: {
    OK: 200,
    CREATED: 201,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    INTERNAL_SERVER_ERROR: 500,
  }
};

// Form Configuration
export const FORM_CONFIG = {
  FIELDS: {
    FULL_NAME: 'fullName',
    EMAIL: 'email',
    PASSWORD: 'password',
    CONFIRM_PASSWORD: 'confirmPassword',
    MOBILE_NUMBER: 'mobileNumber',
    USER_TYPE: 'userType',
    OTP: 'otp',
  },
  USER_TYPES: {
    USER: 'user',
    PANDIT: 'pandit',
    EVENT_PARTNER: 'event_partner',
    SERVICE_PROVIDER: 'service_provider'
  },
  INPUT_TYPES: {
    TEXT: 'text',
    EMAIL: 'email',
    PASSWORD: 'password',
    TEL: 'tel',
    SELECT: 'select',
    RADIO: 'radio',
  },
  BUTTON_TYPES: {
    BUTTON: 'button',
    SUBMIT: 'submit',
    RESET: 'reset',
  },
  VALIDATION_PATTERNS: {
    EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    MOBILE: /^\d{10}$/,
    OTP: /^\d{6}$/,
  }
};

// Text Constants
export const TEXT_CONSTANTS = {
  LABELS: {
    FULL_NAME: 'Full Name',
    EMAIL: 'Email Address',
    PASSWORD: 'Password',
    CONFIRM_PASSWORD: 'Confirm Password',
    MOBILE_NUMBER: 'Mobile Number',
    USER_TYPE: 'I want to sign up as',
    OTP: 'One-Time Password (OTP)',
  },
  PLACEHOLDERS: {
    FULL_NAME: 'Enter your full name',
    EMAIL: 'Enter your email',
    PASSWORD: 'Create a strong password',
    CONFIRM_PASSWORD: 'Confirm your password',
    MOBILE_NUMBER: '9876543210',
    USER_TYPE: 'Select user type',
    OTP: '123456',
  },
  USER_TYPE_OPTIONS: {
    USER: {
      value: 'user',
      label: 'User - Book spiritual services',
      description: 'I want to book poojas and connect with pandits'
    },
    PANDIT: {
      value: 'pandit', 
      label: 'Pandit - Offer spiritual services',
      description: 'I want to offer my spiritual services to users'
    },
    EVENT_PARTNER: {
      value: 'event_partner',
      label: 'Event Partner - Manage event packages',
      description: 'I want to coordinate event-level spiritual services'
    },
    SERVICE_PROVIDER: {
      value: 'service_provider',
      label: 'Service Provider - Offer related services',
      description: 'I want to provide add-on services like decor or music'
    }
  },
  BUTTONS: {
    CREATE_ACCOUNT: 'Create Account',
    CREATING_ACCOUNT: 'Creating Account...',
    SEND_OTP: 'Send OTP',
    VERIFY_CREATE: 'Verify & Create Account',
    BACK_TO_DETAILS: 'Back to details',
  },
  MESSAGES: {
    SUCCESS: {
      ACCOUNT_CREATED: 'Account created successfully! Please check your email for verification.',
      OTP_SENT: 'OTPs sent to {mobile} and {email}.',
    },
    ERROR: {
      EMAIL_EXISTS: 'A user with this email address already exists.',
      INVALID_INPUT: 'Please check your input and try again.',
      REGISTRATION_FAILED: 'Registration failed. Please try again.',
      INVALID_OTP: 'Invalid OTP or an error occurred.',
      GENERIC: 'An error occurred',
    },
    VALIDATION: {
      REQUIRED: '{field} cannot be empty',
      EMAIL_INVALID: 'Email should be valid',
      PASSWORD_MIN_LENGTH: 'Password must be at least {min} characters long',
      PASSWORD_COMPLEXITY: 'Password must contain at least one lowercase letter, one uppercase letter, and one number',
      PASSWORDS_NO_MATCH: 'Passwords do not match',
      MOBILE_INVALID: 'Please enter a valid 10-digit mobile number',
      OTP_INVALID: 'Please enter a valid 6-digit OTP',
      MIN_LENGTH: 'Minimum {min} characters required',
      INVALID_FORMAT: 'Invalid format',
    }
  },
  TITLES: {
    CREATE_ACCOUNT: 'Create your account',
    POOJA_BOOKING: 'PoojaBooking',
  },
  SUBTITLES: {
    JOIN_PLATFORM: 'Join PoojaBooking to connect with experienced pandits',
    CONNECT_PANDITS: 'Connect with experienced pandits for your spiritual needs',
  },
  PAGES: {
    HOME: {
      SERVICES: {
        BOOK_POOJA: {
          TITLE: 'Book a Pooja',
          DESCRIPTION: 'Find experienced pandits for your religious ceremonies and rituals.'
        },
        BECOME_PANDIT: {
          TITLE: 'Become a Pandit',
          DESCRIPTION: 'Join our platform to offer your spiritual services to the community.'
        },
        LEARN_MORE: {
          TITLE: 'Learn More',
          DESCRIPTION: 'Discover various types of poojas and their significance.'
        }
      }
    }
  },
  LINKS: {
    ALREADY_HAVE_ACCOUNT: 'Already have an account?',
    SIGN_IN_HERE: 'Sign in here',
    LOGIN_PATH: '/login',
  }
};

// Animation and Transition Constants
export const ANIMATION_CONFIG = {
  TRANSITIONS: {
    DEFAULT: 'transition-colors duration-200',
    FAST: 'transition-colors duration-100',
    SLOW: 'transition-colors duration-300',
  },
  ANIMATIONS: {
    SPIN: 'animate-spin',
  }
};

// Layout Constants
export const LAYOUT_CONFIG = {
  GRID: {
    RESPONSIVE: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
  },
  FLEX: {
    CENTER: 'flex items-center justify-center',
    COLUMN: 'flex flex-col',
    BETWEEN: 'flex justify-between',
  },
  SPACING: {
    FORM: 'space-y-4',
    SECTION: 'mb-6',
    SMALL: 'mb-4',
  }
};
